import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
	Badge,
	Box,
	Button,
	Card,
	CardBody,
	CardHeader,
	Flex,
	Stack,
	Table,
	Tbody,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue,
} from "@chakra-ui/react";
import moment from "moment";

import { MONTH_PAGE_SIZE } from "../../../utils/ownerPayments";

import PaymentsRow from "./PaymentsRow";

function PaymentsTable({ payments, propertyId, address }) {
	const { t } = useTranslation();
	const [visibleCount, setVisibleCount] = useState(MONTH_PAGE_SIZE);
	const visible = payments.slice(0, visibleCount);
	const hiddenCount = payments.length - visible.length;

	return (
		<Box overflowX="auto">
			<Table variant="simple" size="sm">
				<Thead>
					<Tr>
						<Th pl="0px">{t("table.issuedDate")}</Th>
						<Th>{t("table.status")}</Th>
						<Th>{t("table.amount")}</Th>
						<Th>{t("table.method")}</Th>
						<Th>{t("table.type")}</Th>
						<Th>{t("table.paidDate")}</Th>
						<Th />
					</Tr>
				</Thead>
				<Tbody>
					{visible.map((payment, index) => (
						<PaymentsRow
							key={payment.id || index}
							id={payment.id}
							date={payment.issuedDate}
							address={address}
							showAddress={false}
							paymentDate={payment.paymentDate}
							code={payment.number}
							amount={payment.amount}
							type={payment.type}
							status={payment.paymentDate ? "paid" : "due"}
							logo={payment.logo}
							format={payment.format}
							method={payment.paymentMethod}
							documents={payment.documents}
							paymentDirection={payment.paymentDirection}
							currency={payment.currency}
							propertyId={propertyId}
						/>
					))}
				</Tbody>
			</Table>
			{hiddenCount > 0 && (
				<Button size="xs" variant="link" mt={2} onClick={() => setVisibleCount(visibleCount + MONTH_PAGE_SIZE)}>
					{t("payments.showMore", { count: hiddenCount })}
				</Button>
			)}
		</Box>
	);
}

function PaymentsGroup({ label, count, colorScheme, defaultOpen, children }) {
	const [isOpen, setIsOpen] = useState(Boolean(defaultOpen));
	const groupBg = useColorModeValue("gray.50", "whiteAlpha.100");
	const { t } = useTranslation();

	return (
		<Box borderWidth="1px" borderRadius="md">
			<Flex
				as="button"
				type="button"
				width="100%"
				alignItems="center"
				gap={2}
				px={3}
				py={2}
				bg={groupBg}
				borderTopRadius="md"
				aria-expanded={isOpen}
				aria-label={isOpen ? t("payments.collapseGroup", { label }) : t("payments.expandGroup", { label })}
				onClick={() => setIsOpen(!isOpen)}
			>
				<Box as={isOpen ? ChevronDownIcon : ChevronRightIcon} boxSize={4} />
				<Text fontWeight="semibold" fontSize="sm">
					{label}
				</Text>
				<Badge colorScheme={colorScheme || "gray"}>{count}</Badge>
			</Flex>
			{isOpen && <Box px={3} pb={3}>{children}</Box>}
		</Box>
	);
}

export default function PropertyPaymentsCard({ entry, year, isExpanded, onToggle }) {
	const { t } = useTranslation();
	const months = moment.months();
	const muted = useColorModeValue("gray.600", "gray.300");
	const hasNothingThisYear = entry.months.length === 0 && entry.undated.length === 0;

	return (
		<Card>
			<CardHeader
				as="button"
				type="button"
				textAlign="left"
				py={3}
				aria-expanded={isExpanded}
				aria-label={isExpanded ? t("payments.collapseProperty") : t("payments.expandProperty")}
				onClick={onToggle}
			>
				<Flex alignItems="center" gap={3} flexWrap="wrap">
					<Box as={isExpanded ? ChevronDownIcon : ChevronRightIcon} boxSize={5} />
					<Text fontSize="md" fontWeight="semibold" flex="1" minW="12rem">
						{entry.address}
					</Text>
					{entry.unpaidCount > 0 && (
						<Badge colorScheme="red">{t("payments.unpaidCount", { count: entry.unpaidCount })}</Badge>
					)}
					<Text fontSize="xs" color={muted}>
						{t("payments.paymentsInYear", { count: entry.yearCount, year })}
					</Text>
				</Flex>
			</CardHeader>

			{isExpanded && (
				<CardBody pt={0}>
					<Stack spacing={3}>
						{entry.unpaidCount > 0 && (
							<PaymentsGroup
								label={t("payments.unpaid")}
								count={entry.unpaidCount}
								colorScheme="red"
								defaultOpen
							>
								<PaymentsTable payments={entry.unpaid} propertyId={entry.propertyId} address={entry.address} />
							</PaymentsGroup>
						)}

						{entry.months.map((month, index) => (
							<PaymentsGroup
								key={`${year}-${month.monthIndex}`}
								label={`${months[month.monthIndex]} ${year}`}
								count={month.payments.length}
								defaultOpen={index === 0}
							>
								<PaymentsTable payments={month.payments} propertyId={entry.propertyId} address={entry.address} />
							</PaymentsGroup>
						))}

						{entry.undated.length > 0 && (
							<PaymentsGroup label={t("payments.unknownDate")} count={entry.undated.length} colorScheme="yellow">
								<PaymentsTable payments={entry.undated} propertyId={entry.propertyId} address={entry.address} />
							</PaymentsGroup>
						)}

						{hasNothingThisYear && (
							<Text fontSize="sm" color={muted}>
								{t("payments.noPaymentsInYear", { year })}
							</Text>
						)}
					</Stack>
				</CardBody>
			)}
		</Card>
	);
}
